import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Button,
    Grid,
    Paper,
    Stack,
    TextField,
    Typography,
    Card,
    CardContent,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth, useLoading, useSnackbar } from "../../Context/Contexts";
import { LabelJournalHeaderViewModel, ListQuery, PageComponentProps } from "../../Types/ExportTypes";
import { useNavigation } from "../../Context/NavigationContext";
import useCachedListQuery from "../../hooks/useCachedListQuery";
import ResponsivePagination from "../../components/ResponsivePagination";
import CenteredListCard from "../../components/List/CenteredListCardLayout";
import ListCardHeader from "../../components/List/ListCardHeader";
import DescriptionIcon from "@mui/icons-material/Description"; // or HistoryEduIcon, ArticleIcon, etc.


const LabelJournalList: React.FC<PageComponentProps> = () => {
    const { authService } = useAuth();
    const { showSnackbar } = useSnackbar();
    const { showLoading, hideLoading } = useLoading();
    const { navigate } = useNavigation();
    const { t } = useTranslation();

    const [journals, setJournals] = useState<LabelJournalHeaderViewModel[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [search, setSearch] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const [query, setQuery] = useCachedListQuery({ page: 0, pageSize: 12, filter: "", orderBy: "ext_Created", orderAsc: false });

    const fetchJournals = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await authService.post<{ items: LabelJournalHeaderViewModel[]; count: number }>("/labelJournalApi/GetLabelJournals", query);
            setJournals(response.items ?? []);
            setTotalCount(response.count ?? 0);
        } catch (error: any) {
            showSnackbar(`${t("common.fetchDataError")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            setIsLoading(false);
        }
    }, [authService, query, showSnackbar, t]);

    useEffect(() => {
        fetchJournals();
    }, [fetchJournals]);

    useEffect(() => {
        if (isLoading) showLoading();
        else hideLoading();
    }, [isLoading, showLoading, hideLoading]);

    const handleSearch = (event: React.FormEvent) => {
        event.preventDefault();
        setQuery({ ...query, page: 0, filter: search });
    };

    const handleCreate = async () => {
        try {
            const journal = await authService.post<LabelJournalHeaderViewModel>("/labelJournalApi/CreateJournal", {});
            navigate({ path: "/inventory/labeljournal/edit", params: { documentNo: journal.no } });
        } catch (error: any) {
            showSnackbar(`${t("common.createError")} : ${error.response?.data ?? error.message}`, "error");
        }
    };

    const openJournal = (journal: LabelJournalHeaderViewModel) => {
        navigate({ path: "/inventory/labeljournal/edit", params: { documentNo: journal.no } });
    };

    return (
        <CenteredListCard title={t("common.itemLabels")}>
  
            <Box sx={{ marginBottom: 2 }}>
                    <Button variant="contained" color="success" size="small" onClick={handleCreate}>
                    {t("common.create")}
                </Button>
            </Box>
            <Box
                component="form"
                    onSubmit={handleSearch}
                sx={{
                    display: "flex",
                    gap: 2,
                    marginBottom: 2,
                }}
            >
                <Grid container spacing={2}>
         
                    <Grid item xs={7} lg={8}>
                       
                                        <TextField
                                            label={t("common.search")}
                                            variant="outlined"
                                            type="text"
                                            fullWidth
                                         onChange={(e) => setSearch(e.target.value)}
                                            disabled={isLoading}
                                        /> 
                    </Grid>
                    <Grid item xs={12} lg={3}>
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={isLoading}
                        >
                            {t("common.search")}
                        </Button>
                    </Grid>
                </Grid>
            </Box>


            <Grid container spacing={2} sx={{ mb: 2 }}>
                {journals.map((journal) => (
                    <Grid
                        item
                        xs={12}
                        sm={journals.length === 1 ? 12 : journals.length === 2 ? 6 : 6}
                        md={journals.length === 1 ? 12 : journals.length === 2 ? 6 : 4}
                        key={journal.no}
                    >
                        <Card
                            onClick={() => openJournal(journal)}
                            sx={{
                                cursor: "pointer",
                                height: "100%",
                                transition: "box-shadow 0.2s ease-in-out",
                                "&:hover": {
                                    boxShadow: 4,
                                    backgroundColor: "secondary.main",
                                },
                            }}
                        >
                            <CardContent>
                                <ListCardHeader
                                    icon={<DescriptionIcon fontSize="small" color="action" />}
                                    title={journal.no}
                                />

                                <Typography variant="body1">
                                    {journal.description}
                                </Typography>

                                <Typography variant="body2" color="text.secondary">
                                    {t("common.created")}: {new Date(journal.created).toLocaleString()}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
                {!isLoading && journals.length === 0 && (
                    <Grid item xs={12}>
                        <Box textAlign="center">
                            <Typography variant="body2" color="text.secondary">{t("common.noData")}</Typography>
                        </Box>
                    </Grid>
                )}
            </Grid>


                <ResponsivePagination
                    page={query.page}
                    pageSize={query.pageSize}
                    totalCount={totalCount}
                    onPageChange={(newPage) => setQuery({ ...query, page: newPage })}
                    sx={{mt:1} }
                />
         
     
        </CenteredListCard>
    );
};

export default LabelJournalList;